import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDate, BOOKING_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = React.useState(null);
  const [availAssets, setAvailAssets] = React.useState({}); // product_id -> assets[]
  const [selections, setSelections] = React.useState({}); // product_id -> asset_ids[]
  const [prepList, setPrepList] = React.useState([]);

  const loadPrep = React.useCallback(async () => {
    const { data } = await api.get("/warehouse/preparation");
    setPrepList(data);
  }, []);
  const loadBooking = React.useCallback(async () => {
    if (!id) return;
    const { data } = await api.get(`/bookings/${id}`);
    setB(data);
    const init = {};
    const av = {};
    for (const it of data.items || []) {
      init[it.product_id] = it.asset_ids || [];
      const { data: assets } = await api.get("/assets", { params: { product_id: it.product_id, status: "tersedia" } });
      av[it.product_id] = assets;
    }
    setSelections(init); setAvailAssets(av);
  }, [id]);

  React.useEffect(() => { if (!id) loadPrep(); else loadBooking(); }, [id, loadPrep, loadBooking]);

  if (!id) {
    return (
      <div className="space-y-4" data-testid="checkout-index">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Check-Out</h1>
          <p className="text-sm text-neutral-500">Pilih booking untuk proses check-out.</p>
        </div>
        <div className="card-flat overflow-x-auto">
          <table className="w-full data-table">
            <thead><tr><th className="text-left">No.</th><th className="text-left">Pelanggan</th><th className="text-left">Project</th><th className="text-left">Prep</th><th className="text-left">Status</th><th></th></tr></thead>
            <tbody>
              {prepList.map((b) => (
                <tr key={b.id}>
                  <td className="font-mono text-xs">{b.number}</td>
                  <td data-testid={`checkout-list-customer-${b.id}`}>{b.customer_name || "-"}</td>
                  <td>{b.project_name}</td>
                  <td className="text-xs">{formatDate(b.prep_date)}</td>
                  <td><StatusBadge statusMap={BOOKING_STATUS} value={b.status} /></td>
                  <td className="text-right"><Link to={`/checkout/${b.id}`} className="text-green-700 text-xs hover:underline">Check-Out →</Link></td>
                </tr>
              ))}
              {prepList.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-neutral-500">Tidak ada booking untuk di-checkout.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!b) return <div className="text-sm text-neutral-500">Memuat…</div>;

  const toggle = (pid, aid) => {
    const cur = selections[pid] || [];
    const nx = cur.includes(aid) ? cur.filter((x) => x !== aid) : [...cur, aid];
    setSelections({ ...selections, [pid]: nx });
  };

  const submit = async () => {
    try {
      const items = (b.items || []).map((it) => ({
        product_id: it.product_id,
        qty: it.qty,
        asset_ids: selections[it.product_id] || [],
        condition: "Baik",
      }));
      await api.post(`/warehouse/checkout/${id}`, { items });
      toast.success("Check-out selesai");
      nav(`/bookings/${id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="checkout-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">Check-Out</div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{b.number}</h1>
        <p className="text-sm text-neutral-500">Assign aset fisik untuk booking ini.</p>
      </div>

      {(b.items || []).map((it, idx) => (
        <div key={idx} className="card-flat p-4" data-testid={`checkout-item-${idx}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">{it.product_name}</div>
            <div className="text-xs text-neutral-500">Butuh {it.qty} unit · Dipilih {(selections[it.product_id] || []).length}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(availAssets[it.product_id] || []).map((a) => {
              const sel = (selections[it.product_id] || []).includes(a.id);
              return (
                <button key={a.id} onClick={() => toggle(it.product_id, a.id)}
                  className={`text-left border rounded p-2 text-xs transition-colors ${sel ? "border-green-600 bg-green-50" : "border-neutral-200 hover:border-neutral-400"}`}
                  data-testid={`checkout-asset-${a.id}`}>
                  <div className="font-mono">{a.asset_code}</div>
                  <div className="text-neutral-500">SN: {a.serial_number || "-"}</div>
                </button>
              );
            })}
            {(availAssets[it.product_id] || []).length === 0 && (
              <div className="col-span-full text-xs text-red-600">Tidak ada aset tersedia untuk produk ini.</div>
            )}
          </div>
        </div>
      ))}

      <div className="sticky bottom-0 bg-white border-t border-neutral-200 -mx-6 px-6 py-3 flex justify-end">
        <Button onClick={submit} className="bg-green-600 hover:bg-green-700" data-testid="submit-checkout">Selesai Check-Out</Button>
      </div>
    </div>
  );
}
